import { addDays, endOfDay, startOfDay } from '@if/core/date'
import { EFinancingProgram, getELanguageList, getEProvinceList } from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import Button from '@mui/material/Button'
import { useAppSelector } from '@src/data/store'
import { userSelectors } from '@src/data/store/UserStore'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { ChangeEvent, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MultipleSelectCheckmarks, SelectComponent } from '../../../components'
import {
  CreditTaskDashboardFilters,
  getEAssignmentFilterList,
  getEEntryTypeList,
  SelectValueListItem,
} from '../../../data/types'

interface Props {
  filterValues: CreditTaskDashboardFilters
  resetFilter: () => void
  applyFilter: (data: CreditTaskDashboardFilters) => void
  trusteeNames: string[]
  open: boolean
  onCancel: () => void
}

const FilterComponent = ({ filterValues, open, resetFilter, onCancel, applyFilter, trusteeNames }: Props) => {
  const [selectedLoanTypes, setSelectedLoanTypes] = useState<string[]>(filterValues.financingProgramIds)
  const [selectedEntryTypes, setSelectedEntryTypes] = useState<string[]>(filterValues.entryType)
  const [selectedLanguageId, setSelectedLanguageId] = useState<number[]>(filterValues.correspondanceLanguageId)
  const [selectedAssignmentStatus, setselectedAssignmentStatus] = useState<string>(filterValues.assignation)
  const [selectedBankruptcyTrusteeName, setSelectedBankruptcyTrusteeName] = useState<string>(filterValues.trusteeName)
  const [selectedStatesIso, setSelectedStatesIso] = useState<string[]>(filterValues.statesIso)

  const { t } = useTranslation()
  const selectedCompany = useAppSelector(userSelectors.selectedCompany)
  const availableFinancingProgramOptions = useMemo<SelectValueListItem[]>(
    () =>
      getProgramsByCompanyId([selectedCompany!])
        .filter((x) => x !== EFinancingProgram.IfXpress)
        .map((x) => ({ label: t(`EFinancingProgram.${x}`, { ns: 'enum' }), value: x })),
    [selectedCompany, t],
  )
  const [dateRange, setSelectedDateRange] = useState<string>(filterValues.dateRange)

  const reset = () => {
    resetFilter()
  }

  const dateRanges: SelectValueListItem[] = useMemo(
    () => [
      {
        label: t('taskManager.tasksForOption.now'),
        value: `@${formatUtcIsoDateTime(new Date())}`,
      },
      {
        label: t('taskManager.tasksForOption.tomorrow'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), 1)))}@${formatUtcIsoDateTime(
          endOfDay(addDays(new Date(), 1)),
        )}`,
      },
      {
        label: t('taskManager.tasksForOption.inTwoDays'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), 2)))}@${formatUtcIsoDateTime(
          endOfDay(addDays(new Date(), 2)),
        )}`,
      },
    ],
    [t],
  )

  useEffect(() => {
    setSelectedLoanTypes(filterValues.financingProgramIds)
    setSelectedEntryTypes(filterValues.entryType)
    setSelectedLanguageId(filterValues.correspondanceLanguageId)
    setselectedAssignmentStatus(filterValues.assignation)
    setSelectedBankruptcyTrusteeName(filterValues.trusteeName)
    setSelectedStatesIso(filterValues.statesIso)
    setSelectedDateRange(filterValues.dateRange)
  }, [filterValues])

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
    >
      <DialogTitle id="alert-dialog-title">{t('common.filter')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('editCreditApplication.financingProgramType')}
              content={availableFinancingProgramOptions}
              defaultValues={selectedLoanTypes}
              onValueChanged={(financingProgram: number[] | string[]) => {
                setSelectedLoanTypes(financingProgram as string[])
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('taskManager.taskType')}
              content={getEEntryTypeList().map((value) => ({ label: t(`taskManager.${value}`), value }))}
              defaultValues={selectedEntryTypes}
              onValueChanged={(entryType: number[] | string[]) => {
                setSelectedEntryTypes(entryType as string[])
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <SelectComponent
              items={getEAssignmentFilterList().map((value) => ({
                label: t(`enum.eAssignmentFilter.${value}`),
                value,
              }))}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setselectedAssignmentStatus(event.target.value)
              }}
              label={t('taskManager.applicationAssignment')}
              value={selectedAssignmentStatus}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <SelectComponent
              items={Object.values(trusteeNames).map((value) => ({
                label: value,
                value,
              }))}
              onChange={(e: { target: { value: SetStateAction<string> } }) =>
                setSelectedBankruptcyTrusteeName(e.target.value)
              }
              value={selectedBankruptcyTrusteeName}
              label={t('taskManager.bankruptcyTrusteeNames')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('credit.languageId')}
              content={getELanguageList().map((value) => ({
                label: t(`ELanguage.${value}`, { ns: 'enum' }),
                value,
              }))}
              defaultValues={selectedLanguageId}
              onValueChanged={(languageIds: number[] | string[]) => {
                setSelectedLanguageId(languageIds as number[])
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              content={getEProvinceList().map((value) => ({ label: value, value }))}
              onValueChanged={(e: string[] | number[]) => setSelectedStatesIso(e as string[])}
              defaultValues={selectedStatesIso}
              title="Province"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <SelectComponent
              items={dateRanges}
              label={t('taskManager.tasksFor')}
              onChange={(e: { target: { value: SetStateAction<string> } }) => setSelectedDateRange(e.target.value)}
              value={dateRange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonGroup>
          <Button
            variant="outlined"
            startIcon={<ClearIcon fontSize="small" color="error" />}
            onClick={() => {
              onCancel()
              reset()
            }}
          >
            {t('taskManager.reset')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowRightIcon fontSize="small" color="success" />}
            onClick={() => {
              applyFilter({
                ...filterValues,
                offset: 0,
                financingProgramIds: selectedLoanTypes,
                entryType: selectedEntryTypes,
                correspondanceLanguageId: selectedLanguageId,
                assignation: selectedAssignmentStatus,
                trusteeName: selectedBankruptcyTrusteeName,
                statesIso: selectedStatesIso,
                dateRange,
              })
              onCancel()
            }}
          >
            {t('taskManager.apply')}
          </Button>
        </ButtonGroup>
      </DialogActions>
    </Dialog>
  )
}

export default FilterComponent
